import { Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth/auth.guard';
import { LoginGuard } from '@core/guards/login/login.guard';
import { RoleGuard } from '@core/guards/role/role.guard';
import { MainLayoutComponent } from '@core/layout/main-layout/main-layout.component';
import { AccountDeletionComponent } from '@feature/account-deletion/account-deletion.component';
import { ForgotPasswordComponent } from '@feature/auth/components/forgot-password/forgot-password.component';
import { LoginComponent } from '@feature/auth/components/login/login.component';
import { ResetPasswordComponent } from '@feature/auth/components/reset-password/reset-password.component';
import { VerifyOtpComponent } from '@feature/auth/components/verify-otp/verify-otp.component';
import { PartnerListComponent } from '@feature/dashboard/partners/components/partner-list/partner-list.component';
import { UpsertPartnerComponent } from '@feature/dashboard/partners/components/upsert-partner/upsert-partner.component';
import { ProfileComponent } from '@feature/dashboard/profile/components/profile/profile.component';
import { RecipientDetailsComponent } from '@feature/dashboard/recipients/components/recipient-details/recipient-details.component';
import { RecipientListComponent } from '@feature/dashboard/recipients/components/recipient-list/recipient-list.component';
import { UpsertRecipientComponent } from '@feature/dashboard/recipients/components/upsert-recipient/upsert-recipient.component';
import { SenderDetailsComponent } from '@feature/dashboard/senders/components/sender-details/sender-details.component';
import { SenderListComponent } from '@feature/dashboard/senders/components/sender-list/sender-list.component';
import { UploadSenderComponent } from '@feature/dashboard/senders/components/upload-sender/upload-sender.component';
import { UpsertSenderComponent } from '@feature/dashboard/senders/components/upsert-sender/upsert-sender.component';
import { MasterAdapterSettingsComponent } from '@feature/dashboard/settings/adapter-settings/components/master-adapter-settings/master-adapter-settings.component';
import { MasterPartnerAdapterSettingsComponent } from '@feature/dashboard/settings/adapter-settings/components/master-partner-adapter-settings/master-partner-adapter-settings.component';
import { CountriesComponent } from '@feature/dashboard/settings/country-settings/components/countries/countries.component';
import { UpsertConversionRateComponent } from '@feature/dashboard/settings/country-settings/components/upsert-conversion-rate/upsert-conversion-rate.component';
import { UpsertDestinationCountryComponent } from '@feature/dashboard/settings/country-settings/components/upsert-destination-country/upsert-destination-country.component';
import { UpsertSourceCountryComponent } from '@feature/dashboard/settings/country-settings/components/upsert-source-country/upsert-source-country.component';
import { MasterPartnerTransactionSettingsComponent } from '@feature/dashboard/settings/transaction-settings/components/master-partner-transaction-settings/master-partner-transaction-settings.component';
import { MasterTransactionSettingsComponent } from '@feature/dashboard/settings/transaction-settings/components/master-transaction-settings/master-transaction-settings.component';
import { PartnerTransactionSettingsComponent } from '@feature/dashboard/settings/transaction-settings/components/partner-transaction-settings/partner-transaction-settings.component';
import { UpsertVelocityLimitComponent } from '@feature/dashboard/settings/velocity-limit/components/upsert-velocity-limit/upsert-velocity-limit.component';
import { VelocityLimitListComponent } from '@feature/dashboard/settings/velocity-limit/components/velocity-limit-list/velocity-limit-list.component';
import { CreateTransactionComponent } from '@feature/dashboard/transactions/components/create-transaction/create-transaction.component';
import { TransactionDetailsComponent } from '@feature/dashboard/transactions/components/transaction-details/transaction-details.component';
import { TransactionListComponent } from '@feature/dashboard/transactions/components/transaction-list/transaction-list.component';
import { UploadTransactionComponent } from '@feature/dashboard/transactions/components/upload-transaction/upload-transaction.component';
import { PermissionListComponent } from '@feature/dashboard/user-management/permissions/components/permission-list/permission-list.component';
import { UpsertPermissionComponent } from '@feature/dashboard/user-management/permissions/components/upsert-permission/upsert-permission.component';
import { RoleListComponent } from '@feature/dashboard/user-management/roles/components/role-list/role-list.component';
import { UpsertRoleComponent } from '@feature/dashboard/user-management/roles/components/upsert-role/upsert-role.component';
import { UpsertUserComponent } from '@feature/dashboard/user-management/users/components/upsert-user/upsert-user.component';
import { UserListComponent } from '@feature/dashboard/user-management/users/components/user-list/user-list.component';
import { WalletListComponent } from '@feature/dashboard/wallets/components/wallet-list/wallet-list.component';
import { NotFoundComponent } from '@feature/not-found/not-found.component';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: 'auth/login' },
  {
    path: 'auth',
    children: [
      { path: 'login', component: LoginComponent, canActivate: [LoginGuard] },
      {
        path: ':type/verify-otp',
        component: VerifyOtpComponent,
        canActivate: [LoginGuard],
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordComponent,
        canActivate: [LoginGuard],
      },
      {
        path: 'reset-password',
        component: ResetPasswordComponent,
        canActivate: [LoginGuard],
      },
    ],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'profile',
        component: ProfileComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'partner/list',
        component: PartnerListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'partner/add',
        component: UpsertPartnerComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'partner/edit/:id',
        component: UpsertPartnerComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'wallet/list',
        component: WalletListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'transaction/list',
        component: TransactionListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'transaction/details/:id',
        component: TransactionDetailsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'transaction/create',
        component: CreateTransactionComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'transaction/upload',
        component: UploadTransactionComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'sender/list',
        component: SenderListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'sender/details/:id',
        component: SenderDetailsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'sender/add',
        component: UpsertSenderComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'sender/edit/:id',
        component: UpsertSenderComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'sender/upload',
        component: UploadSenderComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'recipient/list',
        component: RecipientListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'recipient/details/:id',
        component: RecipientDetailsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'recipient/add',
        component: UpsertRecipientComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'recipient/edit/:id',
        component: UpsertRecipientComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'master-settings/transaction-settings',
        component: MasterTransactionSettingsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'master-settings/adapter-settings',
        component: MasterAdapterSettingsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'master-partner-settings/transaction-settings',
        component: MasterPartnerTransactionSettingsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'master-partner-settings/adapter-settings',
        component: MasterPartnerAdapterSettingsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/country',
        component: CountriesComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/add-source-country',
        component: UpsertSourceCountryComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/edit-source-country',
        component: UpsertSourceCountryComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/add-destination-country',
        component: UpsertDestinationCountryComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/edit-destination-country',
        component: UpsertDestinationCountryComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/add-conversion-rate',
        component: UpsertConversionRateComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/edit-conversion-rate',
        component: UpsertConversionRateComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/transaction-settings',
        component: PartnerTransactionSettingsComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/velocity-limit/list',
        component: VelocityLimitListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/velocity-limit/add',
        component: UpsertVelocityLimitComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'settings/velocity-limit/edit/:id',
        component: UpsertVelocityLimitComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/user/list',
        component: UserListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/user/add',
        component: UpsertUserComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/user/edit/:id',
        component: UpsertUserComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/role/list',
        component: RoleListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/role/add',
        component: UpsertRoleComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/role/edit/:id',
        component: UpsertRoleComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/permission/list',
        component: PermissionListComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/permission/add',
        component: UpsertPermissionComponent,
        canActivate: [RoleGuard],
      },
      {
        path: 'user-management/permission/edit/:id',
        component: UpsertPermissionComponent,
        canActivate: [RoleGuard],
      },
    ],
  },
  { path: 'account-deletion', component: AccountDeletionComponent },
  { path: '**', component: NotFoundComponent },
];
